import { forwardRef, useEffect, useState } from "react"
import { Badge } from "@ui/shad"
import { cn } from "@ui/utils"
import { Copy } from "lucide-react"
import { useCopyToClipboard } from "react-use"

interface ButtonBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  text: string
  onClick?: (e: React.MouseEvent) => void
  copyText?: string
  href?: string
}

const ButtonBadge = forwardRef<HTMLDivElement, ButtonBadgeProps>(
  ({ text, onClick, onKeyDown, copyText, href, className, children, ...props }, ref) => {
    const [, copyToClipboard] = useCopyToClipboard()
    const [showCopied, setShowCopied] = useState(false)
    const clickable = !!onClick || !!href

    const handleCopyClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      copyToClipboard(copyText || "")
      setShowCopied(true)
    }

    useEffect(() => {
      if (showCopied) {
        const timer = setTimeout(() => {
          setShowCopied(false)
        }, 2000)
        return () => clearTimeout(timer)
      }
    }, [showCopied])

    const badgeContent = (
      <Badge
        ref={ref}
        data-button-badge
        variant="outline"
        clickable={clickable}
        role={onClick && !href ? "button" : undefined}
        tabIndex={onClick && !href ? 0 : undefined}
        onKeyDown={(event) => {
          onKeyDown?.(event)
          if (!event.defaultPrevented && onClick && !href && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault()
            event.stopPropagation()
            event.currentTarget.click()
          }
        }}
        onClick={(e) => {
          if (onClick) {
            if (href) {
              e.preventDefault()
            }
            onClick(e)
          }
        }}
        className={cn(
          "group border-blue-border bg-blue-background text-blue-foreground hover:bg-blue-background-hover relative inline-flex items-center truncate transition-all duration-200",
          clickable && "hover:bg-blue-background-hover hover:text-blue-foreground",
          className
        )}
        {...props}
      >
        {children ?? <span className="truncate">{text}</span>}
        {!!copyText && (
          <>
            {showCopied ? (
              <span className="bg-background text-foreground absolute top-0.5 right-0.5 rounded-sm px-1 text-xs font-normal transition-all duration-200 ease-out group-hover:opacity-100">
                Copied!
              </span>
            ) : (
              <div
                className="bg-background group-[&:hover]:text-foreground invisible absolute top-0.5 right-0 bottom-0.5 w-6 translate-x-full cursor-pointer rounded-sm opacity-0 transition-all duration-200 ease-out group-[&:hover]:visible group-[&:hover]:-translate-x-0.5 group-[&:hover]:opacity-100"
                onClick={handleCopyClick}
              >
                <Copy className="absolute top-1/2 left-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 cursor-pointer" />
              </div>
            )}
          </>
        )}
      </Badge>
    )

    if (href) {
      return (
        <a href={href} data-button-badge className="inline-flex">
          {badgeContent}
        </a>
      )
    }

    return badgeContent
  }
)

ButtonBadge.displayName = "ButtonBadge"

export { ButtonBadge }
